// キャンバス全体像の PDF スナップショットをクライアントで生成する（Connect Note 連携用）。
// 既存の高解像度ラスタ描画（renderCanvasExportImage）を流用し、pdf-lib で 1 ページ PDF に埋め込む。
// Lambda での PDF 描画（日本語フォント問題）を避けるため、ブラウザの Canvas2D 描画を正本とする。
//
// ページ寸法は常に「埋め込みラスタの画素数をそのままポイント値にする」規則で作る。1 画素 = 1 ポイントに
// なるため、ページ内でラスタが追加縮小されず、保持した画素数がそのまま拡大表示に使える。
//
// 埋め込み形式は経路で分かれる。
// - 自動保存スナップショット（renderCanvasExportPdfBlob）: 幅 2000px の PNG 埋め込み。上限バイトによる自動縮小あり。
// - 手動ダウンロード（renderCanvasDownloadPdfBlob）: 解像度ラダーを上から辿り、EXPORT_WIDTH_PX を超える段は
//   JPEG 埋め込み（/DCTDecode で生バイト列をそのまま格納）、最終段（EXPORT_WIDTH_PX）は現行と同じ PNG 埋め込み。
//   等倍級のラスタを pdf-lib の PNG 埋め込みへ通すと、全画素デコードと数百 MB の JS deflate で
//   メインスレッドが停止するため、大きい段だけデコード不要な JPEG 経路へ寄せている。

import type { CanvasFrame, CanvasObject } from "@refinement-canvas/domain";
import { PDFDocument } from "pdf-lib";
import {
  EXPORT_WIDTH_PX,
  MIN_EXPORT_WIDTH_PX,
  buildManualExportWidthLadder,
  renderCanvasExportImage,
  type ExportRasterFormat,
} from "./canvas-pdf-export";
import type { LinkCardImageResolver } from "./canvas-raster-images";

/** PDF 生成の各経路が受け取る共通オプション。 */
export interface CanvasPdfRenderOptions {
  /** リンクカードの imageUrl を読み込み可能な URL へ解決する（未指定なら読み込まない）。 */
  readonly resolveLinkCardImageUrl?: LinkCardImageResolver | undefined;
}

// 自動縮小の縮小係数（毎回この割合まで幅を下げる）と最大試行回数。EXPORT_WIDTH_PX(3508) から
// MIN_EXPORT_WIDTH_PX(1000) まで 0.75 ずつ下げると 5 段で到達するため、6 回あれば下限まで届く。
const DOWNSCALE_FACTOR = 0.75;
const MAX_DOWNSCALE_ATTEMPTS = 6;
// 自動保存スナップショット（Connect Note 連携の補助プレビュー）の既定描画幅。手動 PDF エクスポート
// （EXPORT_WIDTH_PX=3508、A4 横 300DPI 相当）より低くして、約 20 秒ごとに走るメインスレッドのラスタライズ
// （renderCanvasExportImage の toDataURL）コストと downscaleToFit の反復回数を抑える。全体像は縮小表示の
// 補助プレビューで、詳細はキャンバスを開いて確認する用途のため、この解像度で十分。手動エクスポートは全解像度のまま。
const SNAPSHOT_EXPORT_WIDTH_PX = 2000;

/** data URL から画像バイト列と MIME を取り出す。png / jpeg 以外と不正形式は null。pdf-lib は Uint8Array を要求する。 */
function imageBytesFromDataUrl(
  dataUrl: string
): { readonly mime: "image/png" | "image/jpeg"; readonly bytes: Uint8Array } | null {
  // data:image/{png,jpeg};base64 の base64 本体を 1 回だけデコードする。
  // 検証条件（ヘッダ一致かつ本体が空でない）は PNG 専用だった頃と同じで、対象 MIME を 2 種へ広げただけ。
  // 注: ネイティブデコード（fetch(dataUrl).arrayBuffer()）は、配信時 CSP の connect-src が data: を許可して
  // いない（'self' idp https: wss:）ため enforce 昇格時にブロックされ得る。よって atob ベースのまま残し、
  // メインスレッド負荷は自動保存スナップショットの描画幅低減（SNAPSHOT_EXPORT_WIDTH_PX）で抑える。
  const [header, body] = dataUrl.split(",");
  if (!header || !body) {
    return null;
  }
  const mime = header.startsWith("data:image/png;base64")
    ? ("image/png" as const)
    : header.startsWith("data:image/jpeg;base64")
      ? ("image/jpeg" as const)
      : null;
  if (!mime) {
    return null;
  }
  // pdf-lib の JpegEmbedder は new DataView(imageData.buffer) でバッファ全体を参照するため、
  // 別バッファの部分ビューを渡すと SOF の読み取り位置がずれる。byteOffset 0 の独立バッファを返す。
  const binary = atob(body);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return { mime, bytes };
}

/** 指定描画幅・指定形式で全体像 1 ページ PDF の Blob を生成する。描画失敗時は null。 */
async function renderPdfBlobAtWidth(
  objects: readonly CanvasObject[],
  canvasRect: CanvasFrame,
  exportWidthPx: number,
  // 未確定のローカル専用画像（blob: URL の本人プレビュー）を全体像へ描かないか。
  // 既定 true は自動保存スナップショット（Connect Note へ外部配信）向け。手動ダウンロードでは false。
  excludeUnpublishedSrc = true,
  // ラスタの要求形式。既定 PNG は自動保存スナップショットと解像度ラダー最終段（現行と同じ経路）向け。
  format: ExportRasterFormat = "png",
  // リンクカード画像の解決関数。React の外で描くため、呼び出し元（React 側）から受け取る。
  resolveLinkCardImageUrl?: LinkCardImageResolver | undefined
): Promise<Blob | null> {
  // 全体像スナップショットは Connect Note へ外部配信されるため、未確定のローカル専用画像（blob: URL の
  // 本人用プレビュー。サーバ未公開で他参加者にも配信されない）は描かない（未公開の本人ローカル画像を外部へ
  // 載せない）。公開 URL へ finalize 済みの画像は通常どおり描画する。
  const rasterDataUrl = await renderCanvasExportImage(objects, canvasRect, exportWidthPx, {
    excludeUnpublishedSrc,
    format,
    resolveLinkCardImageUrl,
  });
  if (!rasterDataUrl) {
    return null;
  }
  const decoded = imageBytesFromDataUrl(rasterDataUrl);
  if (!decoded) {
    return null;
  }
  const pdf = await PDFDocument.create();
  // 形式は data URL の実際の MIME で分岐する。要求形式との不一致は renderCanvasExportImage 側で
  // 既に弾かれているため、ここは防御的な二重化にあたる。
  const image =
    decoded.mime === "image/jpeg"
      ? await pdf.embedJpg(decoded.bytes)
      : await pdf.embedPng(decoded.bytes);
  // ラスタの実寸でページを作り、余白 0 で全面に配置する（全体像をそのまま 1 ページに収める）。
  const page = pdf.addPage([image.width, image.height]);
  page.drawImage(image, { x: 0, y: 0, width: image.width, height: image.height });
  const bytes = await pdf.save();
  return new Blob([bytes], { type: "application/pdf" });
}

/**
 * `generate(width)` を startWidthPx から段階的に縮小しながら呼び、生成 Blob が maxBytes 以内に
 * 収まった時点で返す。下限幅（MIN_EXPORT_WIDTH_PX）まで縮めても収まらない場合は、その下限幅の Blob を
 * ベストエフォートで返す（呼び出し側が最終的な上限超過を判断する）。maxBytes 未指定なら初回（最大解像度）を返す。
 * 描画失敗（null）はそのまま null を返す。レンダリングへの依存を持たない純粋なループとしてテスト可能にする。
 *
 * 現在の利用者は自動保存スナップショット（開始幅 SNAPSHOT_EXPORT_WIDTH_PX）だけである。手動出力は
 * 上限バイトを必要としないローカル保存であり、解像度ラダー（buildManualExportWidthLadder）を使うため
 * 本関数を経由しない。既定の startWidthPx は歴史的な既定値として EXPORT_WIDTH_PX のまま残す。
 */
export async function downscaleToFit(
  generate: (exportWidthPx: number) => Promise<Blob | null>,
  maxBytes: number | undefined,
  startWidthPx: number = EXPORT_WIDTH_PX
): Promise<Blob | null> {
  let width = startWidthPx;
  for (let attempt = 0; attempt < MAX_DOWNSCALE_ATTEMPTS; attempt += 1) {
    const blob = await generate(width);
    if (!blob) {
      return null;
    }
    if (maxBytes === undefined || blob.size <= maxBytes || width <= MIN_EXPORT_WIDTH_PX) {
      return blob;
    }
    width = Math.max(MIN_EXPORT_WIDTH_PX, Math.round(width * DOWNSCALE_FACTOR));
  }
  // 現状の定数（DOWNSCALE_FACTOR=0.75 / MIN_EXPORT_WIDTH_PX=1000 / MAX_DOWNSCALE_ATTEMPTS=6）では、
  // 手動エクスポートの開始幅 3508 は 3508→2631→1973→1480→1110→1000 と推移し最後の試行（attempt=5）で
  // width=MIN に達し、自動保存スナップショットの開始幅 2000 は 2000→1500→1125→1000 とより早く MIN に達する。
  // いずれもループ内の `width <= MIN_EXPORT_WIDTH_PX` で必ず return するため、この末尾 return には到達しない。
  // 定数や開始幅を変更し、下限幅へ到達する前に試行回数を使い切る構成にした場合の安全網として、下限幅で
  // ベストエフォート生成して返す（現状到達不能だが、関数の戻り値保証と将来のガードのために残す）。
  return generate(MIN_EXPORT_WIDTH_PX);
}

/**
 * キャンバスオブジェクト群から、全体像 1 ページ PDF（application/pdf）の Blob を生成する。
 * maxBytes を指定すると、大規模キャンバスで PDF が上限を超える場合は描画幅を段階的に下げて上限内へ
 * 収める（古い世代で凍結させず、低解像度でも最新の全体像を保存できるようにする）。
 * 描画に失敗した場合（PNG 生成不可など）は null を返す。
 */
export async function renderCanvasExportPdfBlob(
  objects: readonly CanvasObject[],
  canvasRect: CanvasFrame,
  maxBytes?: number,
  options?: CanvasPdfRenderOptions
): Promise<Blob | null> {
  // 自動保存スナップショットは手動エクスポートより低い開始幅で描画し、メインスレッド占有を抑える。
  // 外部配信のため未確定のローカル専用画像は描かない（excludeUnpublishedSrc=true 既定）。
  return downscaleToFit(
    (width) =>
      renderPdfBlobAtWidth(
        objects,
        canvasRect,
        width,
        true,
        "png",
        options?.resolveLinkCardImageUrl
      ),
    maxBytes,
    SNAPSHOT_EXPORT_WIDTH_PX
  );
}

/**
 * 手動 PDF ダウンロード用に、表示中の全オブジェクトから全体像 1 ページ PDF（application/pdf）の Blob を生成する。
 * 自動保存スナップショットとの違い:
 * - 解像度ラダー（buildManualExportWidthLadder）を上から辿り、キャンバス論理幅と等倍の段から試す（画質優先）。
 * - EXPORT_WIDTH_PX を超える段は JPEG 埋め込み、最終段（EXPORT_WIDTH_PX）は現行と同じ PNG 埋め込みにする。
 * - 未確定のローカル専用画像（本人の blob プレビュー）も描く（印刷ボタンと同じく「表示中の最新状態」を出力する）。
 * ローカル保存で上限バイトの制約が無い経路のため、downscaleToFit（上限バイト調整）は使わない。
 * 全段失敗時は null。
 */
export async function renderCanvasDownloadPdfBlob(
  objects: readonly CanvasObject[],
  canvasRect: CanvasFrame,
  options?: CanvasPdfRenderOptions
): Promise<Blob | null> {
  for (const width of buildManualExportWidthLadder(canvasRect)) {
    // 現行と同じ規模（EXPORT_WIDTH_PX 以下）までは可逆な PNG 埋め込みを使い、
    // それを超える段だけ pdf-lib の全画素デコードを避けるため JPEG 埋め込みにする。
    // 同じキャンバス矩形では画素数は幅の単調増加なので、幅の比較だけで規模を判定できる。
    const format: ExportRasterFormat = width > EXPORT_WIDTH_PX ? "jpeg" : "png";
    try {
      const blob = await renderPdfBlobAtWidth(
        objects,
        canvasRect,
        width,
        false,
        format,
        options?.resolveLinkCardImageUrl
      );
      if (!blob) {
        // その幅では描画・埋め込みが失敗。次段へ降格する。
        console.warn(`[pdf-export] 幅 ${width}px の PDF 生成に失敗したため降格します`);
        continue;
      }
      // 採用した段を 1 行だけ残す（静かな降格を後から判別できるようにする）。
      console.info(`[pdf-export] PDF ダウンロードの出力解像度: ${width}px (${format})`);
      return blob;
    } catch (error) {
      // embedJpg / embedPng の例外もその段の失敗として扱い、次段へ降格する。
      console.warn(`[pdf-export] 幅 ${width}px の PDF 生成で例外が発生したため降格します`, error);
    }
  }
  return null;
}
